import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, shareReplay } from 'rxjs';
import { LookupItem } from 'app/model/lookup.model';
import { BaseResponse } from 'app/core/models/common.response';
import { environment } from 'environments/environment';

@Injectable({
  providedIn: 'root'
})
export class LookupService {

  private readonly _baseUrl = `${environment.baseUrl}/Lookup`;

  /** In-memory cache keyed by lookup type. Each entry is a shared Observable. */
  private readonly _cache = new Map<string, Observable<LookupItem[]>>();

  constructor(private readonly _http: HttpClient) { }

  // -----------------------------------------------------------------------------------------------------
  // @ Cached lookups
  // -----------------------------------------------------------------------------------------------------

  getOrganizations(): Observable<LookupItem[]> {
    return this._getCachedLookup('organizations', `${this._baseUrl}/GetOrganization`);
  }

  getCountries(): Observable<LookupItem[]> {
    return this._getCachedLookup('countries', `${this._baseUrl}/GetCountry`);
  }

  getUserRoles(): Observable<LookupItem[]> {
    return this._getCachedLookup('roles', `${this._baseUrl}/GetUserRoles`);
  }

  getCampCategories(): Observable<LookupItem[]> {
    return this._getCachedLookup('categories', `${this._baseUrl}/GetCampCategory`);
  }

  getAdditionalFees(): Observable<LookupItem[]> {
    return this._getCachedLookup('fees', `${this._baseUrl}/GetAdditionalFees`);
  }

  getVolunteers(): Observable<LookupItem[]> {
    return this._getCachedLookup('volunteers', `${this._baseUrl}/GetVolunteers`);
  }

  getTrains(): Observable<LookupItem[]> {
    return this._getCachedLookup('trains', `${this._baseUrl}/GetTrains`);
  }

  getUsers(): Observable<LookupItem[]> {
    return this._getCachedLookup('users', `${this._baseUrl}/GetUsers`);
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Dynamic lookups (dependent on parent, NOT cached)
  // -----------------------------------------------------------------------------------------------------

  getStates(countryId: string): Observable<LookupItem[]> {
    return this._http
      .get<BaseResponse<LookupItem[]>>(`${this._baseUrl}/GetState/${countryId}`)
      .pipe(map((res) => res?.result ?? []));
  }

  getCities(stateId: string): Observable<LookupItem[]> {
    return this._http
      .get<BaseResponse<LookupItem[]>>(`${this._baseUrl}/GetCity/${stateId}`)
      .pipe(map((res) => res?.result ?? []));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Cache management
  // -----------------------------------------------------------------------------------------------------

  /** Clear a specific lookup cache entry, or all entries if no key is given. */
  clear(key?: string): void {
    if (key) {
      this._cache.delete(key);
    } else {
      this._cache.clear();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private helpers
  // -----------------------------------------------------------------------------------------------------

  /**
   * Fetches a lookup list from the API and caches the result via `shareReplay(1)`.
   * The API returns `BaseResponse<LookupItem[]>`, so we unwrap `.result` before caching.
   */
  private _getCachedLookup(key: string, apiUrl: string): Observable<LookupItem[]> {
    if (!this._cache.has(key)) {
      const request$ = this._http
        .get<BaseResponse<LookupItem[]>>(apiUrl)
        .pipe(
          map((res) => res?.result ?? []),
          shareReplay(1),
        );

      this._cache.set(key, request$);
    }

    return this._cache.get(key)!;
  }
}
